import type { ColDef, IServerSideDatasource } from "ag-grid-community";
import React from "react";

import { type RouterInputs, trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import {
  actionsColumn,
  dateColumn,
  emailColumn,
  priceColumn,
} from "@/features/admin/utils/columns";
import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";
import { AdminGrid, denseAdminGridTheme } from "../AdminGrid";
import {
  type MasterInvoiceTableRow as Row,
  toInvoiceAdminFilterModel,
  toMasterInvoiceTableRows,
} from "./invoiceTableRows";

const PAGE_SIZE = 50;
type MasterInvoiceFilterModel =
  RouterInputs["admin"]["billing"]["masterInvoices"]["filterModel"];

export function MasterInvoicesTable() {
  const utils = trpc.useUtils();
  const dataSource = useMasterInvoicesDataSource();
  const { gridProps } = useAdminServerSideGrid<Row>({
    serverSideDatasource: dataSource,
    paginationPageSize: PAGE_SIZE,
  });

  const columnDefs = React.useMemo<ColDef<Row>[]>(
    () => [
      {
        colId: "invoice",
        headerName: "Invoice",
        maxWidth: 160,
        minWidth: 120,
        sortable: false,
        cellRenderer: ({ data }: { data?: Row }) => {
          if (!data) return null;
          if (data.kind === "pending") {
            return (
              <Badge variant="soft" color="amber" size="sm">
                Pending
              </Badge>
            );
          }
          return (
            <Badge variant="outline" color="gray" size="sm" className="w-14">
              {data.docId}
            </Badge>
          );
        },
      },
      {
        ...dateColumn,
        colId: "createdAt",
        headerName: "Date",
        sortable: false,
        valueGetter: ({ data }) =>
          data?.kind === "completed" ? data.createdAt : null,
      },
      {
        ...emailColumn,
        colId: "recipientEmail",
        headerName: "Recipient",
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params) => {
            try {
              const values =
                await utils.admin.billing.masterInvoiceRecipients.fetch();
              params.success(values);
            } catch (_err) {
              params.fail();
            }
          },
        },
        valueGetter: ({ data }) =>
          data?.kind === "completed" ? (data.recipientEmail ?? "-") : "-",
      },
      {
        ...priceColumn,
        colId: "total",
        headerName: "Total",
        maxWidth: 180,
        filter: false,
        sortable: false,
        valueGetter: ({ data }) => data?.total ?? 0,
      },
      {
        ...actionsColumn,
        colId: "actions",
        headerName: "",
        cellRenderer: ({ data }: { data?: Row }) => {
          if (!data) return null;
          const href =
            data.kind === "pending"
              ? ({ to: "/admin/billing/master-invoices/preview" } as const)
              : ({
                  to: "/admin/billing/master-invoices/$id",
                  params: { id: data.docId },
                } as const);
          return (
            <div className="flex h-full items-center justify-end pe-2">
              <Link
                href={href}
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                View
              </Link>
            </div>
          );
        },
      },
    ],
    [utils],
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 border rounded-lg overflow-hidden">
      <AdminGrid<Row>
        {...gridProps}
        columnDefs={columnDefs}
        theme={denseAdminGridTheme}
        getRowId={({ data }) => data.id}
        defaultColDef={{
          flex: 1,
          resizable: true,
          sortable: false,
          filterParams: { maxNumConditions: 1 },
        }}
        suppressDragLeaveHidesColumns
      />
    </div>
  );
}

function useMasterInvoicesDataSource(): IServerSideDatasource {
  const utils = trpc.useUtils();

  return React.useMemo(
    () => ({
      getRows: async (params) => {
        const { startRow, endRow, filterModel } = params.request;

        try {
          const data = await utils.admin.billing.masterInvoices.fetch({
            take: (endRow ?? 0) - (startRow ?? 0),
            skip: startRow ?? 0,
            filterModel:
              toInvoiceAdminFilterModel<MasterInvoiceFilterModel>(filterModel),
          });

          params.success({
            rowData: toMasterInvoiceTableRows(data.data),
            rowCount: data.totalCount,
          });
        } catch (_err) {
          params.fail();
        }
      },
    }),
    [utils],
  );
}
